import React, { useCallback, useMemo } from 'react';
import styled from 'types/theme.types';
import { css, useTheme } from '@emotion/react';
import { legacyOnly, newDesignOnly } from 'constants/themes.constants';
import TextInput from './input-components/text-input';
import type { InputProps } from 'antd/lib/input';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import Tooltip from 'components/common/tooltip';
import debounce from 'lodash/debounce';
import { isTestNodeEnv } from 'constants/config';
import type { InputRef } from 'antd';
import { useIntl } from 'react-intl';
import Markdown from 'components/common/markdown';

export interface SearchBarProps extends InputProps {
  placeholder?: string;
  /** `placeholder` is a translation key, so a parameterised one needs its values. TextInput formats both. */
  placeholderValues?: Record<string, string>;
  onFilter: (query: string) => void;
  debounceMs?: number;
  hideError?: boolean;
  error?: string | null;
  showExtendedSearchSyntax?: boolean;
}

export const DEFAULT_DEBOUNCE = isTestNodeEnv ? 5 : 250;

// eslint-disable-next-line react/display-name
const SearchBar: React.FunctionComponent<SearchBarProps> = React.forwardRef<InputRef, SearchBarProps>(
  (
    {
      onFilter,
      placeholder,
      hideError,
      debounceMs = DEFAULT_DEBOUNCE,
      value,
      error,
      allowClear,
      showExtendedSearchSyntax = false,
      ...otherProps
    }: SearchBarProps,
    ref
  ) => {
    const onFilterWithDebounce = useMemo(() => {
      return debounce(async searchValue => {
        onFilter(searchValue);
      }, debounceMs || 250);
    }, [debounceMs, onFilter]);

    const setSearch = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onFilterWithDebounce(e.target.value);
      },
      [onFilterWithDebounce]
    );

    const intl = useIntl();
    const theme = useTheme();

    const tooltipContent = useMemo(() => {
      let content: string = placeholder ? intl.formatMessage({ id: placeholder }) : '';
      if (showExtendedSearchSyntax) {
        if (content) {
          content += '\n\n---\n\n';
        }
        content += intl.formatMessage({ id: 'search-bar.extended-search-syntax.tooltip' });
      }
      return content;
    }, [placeholder, showExtendedSearchSyntax, intl]);

    // `right` opened clear of a suffix icon at the field's right edge. From the prefix it would open
    // across the field's own text, so the new design drops the 320px overlay below it instead.
    const searchIcon = (
      <Tooltip
        title={<StyledMarkdown markdown={tooltipContent} />}
        placement={theme.isNewDesign ? 'bottomLeft' : 'right'}>
        <SearchOutlined />
      </Tooltip>
    );

    const newDesignAllowClear = allowClear === false ? false : { clearIcon: <CloseOutlined /> };

    return (
      <StyledTextInput
        data-e2e="search-bar"
        error={error}
        placeholder={placeholder}
        hideError={!!hideError}
        allowClear={theme.isNewDesign ? newDesignAllowClear : allowClear}
        {...(theme.isNewDesign ? { prefix: searchIcon } : { suffix: searchIcon })}
        {...otherProps}
        ref={ref}
        onChange={setSearch}
      />
    );
  }
);

const StyledTextInput = styled(TextInput)`
  ${legacyOnly(
    theme => css`
      border: 1px solid ${theme.lightGray} !important;
    `
  )}

  ${newDesignOnly(css`
    .ant-input-prefix .anticon {
      font-size: 16px;
      color: var(--component-field-icon);
    }

    &.ant-input-affix-wrapper-disabled .ant-input-prefix .anticon {
      color: var(--component-field-icon-disabled);
    }
  `)}
`;

const StyledMarkdown = styled(Markdown)`
  &&& p:last-of-type {
    margin-bottom: 0;
  }
`;

export default SearchBar;
